import QuestionTypeIcon from '@/components/question/QuestionTypeIcon';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { BadgeSelect, Button, Select } from '@/components/ui/daisy';
import { ModalView } from '@/data/hook/question/useModalView';
import { BreadcrumbItem } from '@/data/hook/useDynamicBreadcrumbItems';
import Activity from '@/models/Activity';
import { QuestionTypeEnum } from '@/models/Question';
import { accordionMarginTop } from '@/utils/animations/commom';
import { colorsMap } from '@/utils/questions/colors';
import {
  ArrowDownTrayIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import QuestionsModalBreadcrumb from './QuestionsModalBreadcrumb';

type HeaderProps = {
  view: ModalView;
  selectedType: QuestionTypeEnum | null;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  breadcrumbItems?: BreadcrumbItem[];
  activity: Activity;
  onSelectTypology: (type: QuestionTypeEnum) => void;
  onSave: () => void;
  onClose: () => void;
  allowedTypes?: QuestionTypeEnum[];
};

export default function CreateQuestionHeader({
  onClose,
  selectedType,
  breadcrumbItems,
  activity,
  view,
  isSaving,
  hasUnsavedChanges,
  onSelectTypology,
  onSave,
  allowedTypes
}: HeaderProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal',
  });

  const { t: tQuestionType } = useTranslation('translation', {
    keyPrefix: 'questionTypes',
  });

  const typologies = allowedTypes ?? Object.values(QuestionTypeEnum);
  const shouldForceOpen = view === 'create' && (typologies.length ?? 0) > 1;

  return (
    <div className="flex flex-col">
      <div className="flex gap-6 items-center">
        <Button variant="soft" size="sm" onClick={onClose}>
          <ChevronLeftIcon className="w-3 h-3" />
          {t('back')}
        </Button>
        <ConditionalRenderer condition={!!breadcrumbItems?.length}>
          <QuestionsModalBreadcrumb
            items={breadcrumbItems ?? []}
            onClose={onClose}
          />
        </ConditionalRenderer>
      </div>

      <AnimatePresence>
        {view === 'create' && (
          <motion.div
            className="flex items-center justify-between overflow-hidden"
            {...accordionMarginTop}
          >
            <span className="text-base-content font-poppins text-xl">
              {activity.name}
            </span>
            <Button color="primary" disabled>
              <ArrowDownTrayIcon className="h-4 w-4" />
              {t('save')}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex gap-4 justify-between items-center mt-4 flex-wrap">
        <div className="flex flex-col gap-4">
          <span className="text-base font-semibold text-base-content">
            {t('title')}
          </span>
          <div className="flex gap-4 items-center">
            <Select.Wrapper disabled>
              <Select.Label>{t('educationalParameter')}</Select.Label>
              <Select disabled>
                <Select.Option value="">{t('selectPlaceholder')}</Select.Option>
              </Select>
            </Select.Wrapper>

            <BadgeSelect
              forceOpen={shouldForceOpen}
              classNames={{ base: 'w-[530px]' }}
              label={t('typology')}
              value={selectedType ?? undefined}
              onChange={value => onSelectTypology(value as QuestionTypeEnum)}
            >
              {typologies.map(type => (
                <BadgeSelect.Option
                  key={type}
                  value={type}
                  color={colorsMap[type]}
                >
                  <QuestionTypeIcon type={type} className="size-4" />{' '}
                  {tQuestionType(type)}
                </BadgeSelect.Option>
              ))}
            </BadgeSelect>
          </div>
        </div>
        <ConditionalRenderer condition={view === 'editor'}>
          <Button
            color="primary"
            disabled={isSaving || !hasUnsavedChanges}
            loading={isSaving}
            onClick={onSave}
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {t('save')}
          </Button>
        </ConditionalRenderer>
      </div>
    </div>
  );
}
